import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Bell } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/8mGMBFzUfDBa2mdC9g2ZRk/AVY_LOGO.png';
const NAV_ITEMS = [{
  label: 'Image Style',
  href: '/'
}, {
  label: 'Brand Voice',
  href: '/brandvoice'
}, {
  label: 'Visual Language',
  href: '/visuallanguage'
}, {
  label: 'Project Plan',
  href: '/project-plan'
}, {
  label: 'Final Assets',
  href: '/final-assets'
}];
const ThreeDotIcon = () => <div className="flex flex-col items-center justify-center gap-[3px] w-5 h-5" data-id="element-24">
    <div className="w-[5px] h-[5px] bg-ink rounded-full" data-id="element-25" />
    <div className="flex gap-[3px]" data-id="element-26">
      <div className="w-[5px] h-[5px] bg-ink rounded-full" data-id="element-27" />
      <div className="w-[5px] h-[5px] bg-ink rounded-full" data-id="element-28" />
    </div>
  </div>;
const NOTIFICATIONS = [{
  id: 'posters-artwork',
  text: 'Final artwork for posters has been uploaded'
}, {
  id: 'printed-assets-delivered',
  text: 'Booklets and roll-up banners delivered 17 June'
}, {
  id: 'logo-animation',
  text: 'Animation: Logo reveal uploaded'
}, {
  id: 'checklist-updated',
  text: 'Deliverables checklist updated'
}, {
  id: 'image-library',
  text: 'Image library assets have been uploaded'
}, {
  id: 'digital-booklet',
  text: 'Digital PDF version of the booklet has been uploaded'
}, {
  id: 'email-signature',
  text: 'Email signature template has been uploaded'
}];
export function Navigation({
  activePath
}: {
  activePath: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  // Initialize visible notifications by filtering out those stored in localStorage as dismissed
  const [visibleNotifications, setVisibleNotifications] = useState(() => {
    try {
      const dismissed = JSON.parse(localStorage.getItem('headway_dismissed_notifications') || '[]');
      return NOTIFICATIONS.filter(n => !dismissed.includes(n.id));
    } catch (e) {
      return NOTIFICATIONS;
    }
  });
  const dismissNotification = (id: string) => {
    setVisibleNotifications(prev => prev.filter(n => n.id !== id));
    // Persist the dismissal to localStorage
    try {
      const dismissed = JSON.parse(localStorage.getItem('headway_dismissed_notifications') || '[]');
      if (!dismissed.includes(id)) {
        localStorage.setItem('headway_dismissed_notifications', JSON.stringify([...dismissed, id]));
      }
    } catch (e) {
      console.error('Failed to save dismissal', e);
    }
  };
  return <header className="p-4 md:p-6 no-print relative z-[80]" data-id="element-29">
      <div className="flex items-center justify-between gap-2" data-id="element-30">
        <div className="flex items-center gap-2" data-id="element-31">
          <div className="bg-white border border-black/10 rounded-full p-2 h-10 w-10 md:h-12 md:w-12 flex items-center justify-center shadow-sm" data-id="element-32">
            <img src={LOGO_URL} alt="Avy Logo" className="h-5 md:h-6 w-auto" data-id="element-33" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-2" data-id="element-34">
            {NAV_ITEMS.map(item => {
            const isActive = activePath === item.href;
            const isFinalAssets = item.href === '/final-assets';
            let baseClass = 'bg-white text-ink hover:bg-gray-50';
            if (isFinalAssets) {
              baseClass = 'bg-[#d2ff46] text-ink border-[#d2ff46] hover:bg-[#c5f03d]';
            }
            let activeClass = 'bg-primary text-white';
            if (isFinalAssets) {
              activeClass = 'bg-[#d2ff46] text-ink border-[#d2ff46] shadow-md ring-2 ring-[#d2ff46]/20';
            }
            return <a key={item.href} href={item.href} className={`${isActive ? activeClass : baseClass} border border-black/10 rounded-xl px-4 md:px-6 h-10 md:h-12 flex items-center text-xs md:text-sm font-bold shadow-sm transition-all`} data-id="element-35">
                  {item.label}
                </a>;
          })}
          </nav>
        </div>

        <div className="flex items-center gap-4" data-id="element-36">
          <div className="flex items-center gap-3" data-id="element-37">
            <span className="text-lg md:text-xl font-bold tracking-tighter hidden sm:inline-block" data-id="element-38">
              Headway Tuition
            </span>
          </div>

          {/* Notifications Bell */}
          <div className="relative" data-id="element-39">
            <button onClick={() => setShowNotifications(!showNotifications)} className={`p-2 rounded-xl border transition-all duration-200 relative ${showNotifications ? 'bg-[#d2ff46] border-[#d2ff46] text-ink' : 'bg-white border-black/10 text-ink hover:bg-gray-50'}`} aria-label="Toggle notifications" data-id="element-40">
              <Bell size={20} data-id="element-41" />
              {visibleNotifications.length > 0 && <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-accent rounded-full border-2 border-white" data-id="element-42" />}
            </button>

            <AnimatePresence data-id="element-43">
              {showNotifications && <>
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onClick={() => setShowNotifications(false)} className="fixed inset-0 z-10" data-id="element-44" />
                  <motion.div initial={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} className="absolute right-0 mt-3 w-72 md:w-80 bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden z-20" data-id="element-45">
                    <div className="p-4 border-b border-black/5 bg-gray-50/50 flex items-center justify-between" data-id="element-46">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-ink/40" data-id="element-47">
                        Latest Updates
                      </h3>
                      <span className="bg-[#d2ff46] text-ink text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-48">
                        {visibleNotifications.length} New
                      </span>
                    </div>
                    <div className="divide-y divide-black/5 max-h-[300px] overflow-y-auto" data-id="element-49">
                      {visibleNotifications.length > 0 ? visibleNotifications.map(note => <div key={note.id} className="p-4 hover:bg-black/[0.01] transition-colors flex gap-3 items-start group/note" data-id="element-50">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#d2ff46] mt-1.5 shrink-0" data-id="element-51" />
                            <p className="text-xs md:text-sm font-medium text-ink leading-relaxed flex-1" data-id="element-52">
                              {note.text}
                            </p>
                            <button onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      dismissNotification(note.id);
                    }} className="p-1 rounded-md hover:bg-black/5 text-ink/20 hover:text-ink transition-all" aria-label="Dismiss notification" data-id="element-53">
                              <X size={14} data-id="element-54" />
                            </button>
                          </div>) : <div className="p-8 text-center" data-id="element-55">
                          <p className="text-xs font-medium text-ink/30" data-id="element-56">
                            No new updates
                          </p>
                        </div>}
                    </div>
                    <a href="/final-assets" onClick={() => setShowNotifications(false)} className="block p-4 bg-gray-50 text-center text-[10px] font-bold uppercase tracking-widest text-accent hover:bg-gray-100 transition-colors" data-id="element-57">
                      View All Assets
                    </a>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2 bg-white border border-black/10 rounded-2xl shadow-sm text-ink flex items-center justify-center w-12 h-12 relative z-[100]" aria-label="Toggle menu" data-id="element-58">
            <AnimatePresence mode="wait" initial={false} data-id="element-59">
              <motion.div key={isOpen ? 'close' : 'open'} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="flex items-center justify-center" data-id="element-60">
                {isOpen ? <X size={24} data-id="element-61" /> : <ThreeDotIcon data-id="element-62" />}
              </motion.div>
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      <AnimatePresence data-id="element-63">
        {isOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-[#d9e2e8]/80 backdrop-blur-md lg:hidden z-[60]" data-id="element-64" />
            <motion.nav initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.4,
          ease: 'easeInOut'
        }} className="fixed inset-0 bg-[#d9e2e8] lg:hidden z-[70] p-4 pt-24 flex flex-col gap-3 overflow-y-auto" data-id="element-65">
              <div className="flex flex-col gap-3 flex-1" data-id="element-66">
                {NAV_ITEMS.map(item => {
              const isActive = activePath === item.href;
              const isFinalAssets = item.href === '/final-assets';
              let baseStyle = 'bg-[#fff5f0] border-black/5 hover:border-black/10 shadow-sm';
              if (isFinalAssets) {
                baseStyle = 'bg-[#d2ff46] border-[#d2ff46] shadow-sm';
              }
              let activeStyle = 'bg-white border-primary shadow-md';
              if (isFinalAssets) {
                activeStyle = 'bg-[#d2ff46] border-[#d2ff46] shadow-md ring-4 ring-[#d2ff46]/20';
              }
              return <a key={item.href} href={item.href} onClick={() => setIsOpen(false)} className={`
                        relative group flex items-center p-8 rounded-[2rem] border transition-all duration-300
                        ${isActive ? activeStyle : baseStyle}
                      `} data-id="element-67">
                      <span className="text-sm font-bold uppercase tracking-[0.2em] text-ink" data-id="element-68">
                        {item.label}
                      </span>
                    </a>;
            })}

                <a href="mailto:ap@avy.me" className="relative flex items-center p-8 rounded-[2rem] bg-[#2b2b2b] border border-black/5 shadow-lg mt-2" data-id="element-69">
                  <div className="absolute top-6 right-6 w-3 h-3 bg-[#ef3e23] rounded-full shadow-[0_0_10px_rgba(239,62,35,0.5)]" data-id="element-70" />
                  <span className="text-sm font-bold uppercase tracking-[0.2em] text-white" data-id="element-71">
                    FEEDBACK
                  </span>
                </a>
              </div>

              <div className="py-6 text-center" data-id="element-72">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/30" data-id="element-73">
                  Headway Tuition Brand Portal
                </p>
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </header>;
}